import React from 'react';
import { Link } from 'gatsby';

import Button from '../components/Button';
import { DefaultLogoReel } from '../components/LogoReel';
import USPCardRedesigned from '../components/USPCardRedesigned';

const enablingYouToBuildYourBest = [
  {
    image: 'illustrations/marketing-websites-and-seo-pink.svg',
    title: 'Marketing Websites & SEO',
    description:
      'GraphCMS provides you complete flexibility, so you can focus on building exceptional web experiences on your terms, with strong SEO foundations and localization out of the box.',
    ctaTo: '/use-cases/content-localization',
  },
  {
    image: 'illustrations/modern-applications-pink.svg',
    title: 'Modern Applications',
    description:
      'Not all content is marketing. In the age of streaming platforms, SaaS products, omnichannel eCommerce, and IoT, you need more than just a generic CMS.',
    ctaTo: '/use-cases/structured-content',
  },
  {
    image: 'illustrations/portals-and-intranets-pink.svg',
    title: 'Portals and Intranets',
    description:
      'Give customers the information they seek, and employees the information they need, all with using fewer resources to maintain a better service. It’s a win-win.',
    ctaTo: '/use-cases/content-federation',
  },
];

const manageContentExperiences = [
  {
    icon: 'DatabaseLineIcon',
    title: 'Modern Headless CMS',
    description:
      'Manage all of your content at a central place, reducing repetitive work, and deliver across all platforms and devices from a single source.',
  },
  {
    icon: 'ShieldUserLineIcon',
    title: 'Sustainable content strategy',
    description:
      "Build for today's omnichannel requirements and for every platform that is yet to come - deliver content natively to all digital frontends, old and yet unknown, via API.",
  },
  {
    icon: 'TerminalLineIcon',
    title: 'Content management done right',
    description:
      'Collaborate on creating compelling content experiences with your team, with industry leading features, workflows, and permissions.',
  },
];

const richEditingExperience = [
  {
    icon: 'Attachment2Icon',
    title: 'Digital asset management',
    description:
      'Our inbuilt digital asset management helps you to organize your assets. Use the image editor or add custom metadata to your uploaded files.',
  },
  {
    icon: 'TimeLineIcon',
    title: 'Versioning',
    description:
      'Safely retain a version history of your content. Track changes and revert to a previous version when needed.',
  },
  {
    icon: 'CheckboxCircleLineIcon',
    title: 'Validations',
    description:
      'Make sure content creators play by the rules. Validate your inputs like phone numbers, emails, or any other custom pattern.',
  },
];

const draftPreviewPublishRepeat = [
  {
    icon: 'Attachment2Icon',
    title: 'Content staging',
    description:
      'We let you safely work on your content drafts so you can preview everything before it’s published to your live sites and applications.',
  },
  {
    icon: 'EyeLineIcon',
    title: 'Previews',
    description:
      'Set up URLs for all your web environments to instantly see how your content looks like in production.',
  },
  {
    icon: 'RefreshLineIcon',
    title: 'Safe iterations',
    description:
      'Keep on drafting after you have published your content. Publish your new version once ready.',
  },
];

const localizeAllYourContentEasily = [
  {
    icon: 'Translate2Icon',
    title: 'Locale based publishing',
    description:
      'Draft your localizations across any language, at any time, and publish them once ready - either in bulk or individually.',
  },
  {
    icon: 'ArrowGoBackLineIcon',
    title: 'Fallbacks',
    description:
      'Translations not ready but need to publish anyway? Set a fallback locale so incomplete localizations are shown, not left blank.',
  },
  {
    icon: 'LightbulbFlashLineIcon',
    title: 'Automated AI translations',
    description:
      'Our Enterprise plans can be configured to come with automatic AI translations out of the box, so you never have to worry about localization speeds ever again.',
    comingSoon: true,
  },
];

const granularRolesAndPermissions = [
  {
    icon: 'ShieldUserLineIcon',
    title: 'Restrict visibility and access',
    description:
      'Create roles for internal or external collaborators that have restricted access rights for reading or modifying content.',
  },
  {
    icon: 'ShieldCheckLineIcon',
    title: 'Protect your content',
    description:
      'Fine-grained permissions can also be applied to your API. Allow different content sets to be seen for authorized users.',
  },
  {
    icon: 'UserAddLineIcon',
    title: 'Custom roles and permissions',
    description:
      'Need specific permission levels for external Spanish translators or that SEO auditor? Talk to us about setting up custom roles.',
  },
];

const ForContentCreatorsBaseTemplate = ({ pageContext: { page } }) => {
  if (!page) return null;

  const { usps } = page;

  return (
    <React.Fragment>
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-12 md:space-y-0">
            <div className="w-full md:w-2/5 space-y-6">
              <p className="text-rose font-semibold uppercase tracking-wider">
                For content creators
              </p>
              <h1 className="font-bold leading-tight text-3xl md:text-5xl">
                {page.title}
              </h1>
              <p className="leading-relaxed md:text-lg text-zodiac">
                {page.subTitle}
              </p>
              <Button href="https://app.graphcms.com/contact" colour="pink">
                Get enterprise demo &#8594;
              </Button>
            </div>

            <div className="w-full md:w-3/5">
              <div className="relative md:-mr-32">
                <img
                  src="/illustrations/graphcms-for-content-creators.svg"
                  alt=""
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-12 md:pb-6 lg:pt-20">
            {usps.map((usp, index) => (
              <USPCardRedesigned key={index} {...usp} theme="pink" />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <DefaultLogoReel />
        </div>
      </section>

      <section className="bg-haiti py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="md:flex md:items-center space-y-12 md:space-y-0">
            <div className="w-full md:w-1/2 md:pr-12">
              <div className="relative inline-flex items-center pb-4">
                <span className="text-rose font-medium uppercase tracking-widest text-sm">
                  Deliver content at breakneck speeds
                </span>
              </div>
              <h3 className="font-bold text-xl md:text-2xl leading-snug mb-4 text-white">
                "We love GraphCMS. It is one of the (if not the) best Headless
                CMS on the market..."
              </h3>
              <p className="text-whisper mb-6">
                At Travel Weekly, GraphCMS is used to store, manage, update, and
                publish over 100,000 articles. 40 dedicated content editors are
                able to add content, manage assets, style the content exactly as
                they please, and deliver at speed, without relying on any
                roundtrips to the development team.
              </p>
              <Link
                to="/case-studies/travel-weekly-case-study"
                className="font-medium group text-rose"
              >
                Learn about Travel Weekly’s experience
                <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                  &#8594;
                </span>
              </Link>
            </div>

            <div className="w-full md:w-1/2 h-full md:flex md:items-center md:justify-center">
              <img src="/illustrations/imre-nagy-pink.svg" alt="" />
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="pt-6 md:pt-12 lg:pt-24 md:text-center max-w-3xl mx-auto">
            <h3 className="font-bold text-xl md:text-2xl leading-snug mb-4 text-white">
              See How GraphCMS Transforms Your Content Management
            </h3>
            <p className="text-whisper">
              GraphCMS plans are flexibly suited to accommodate your growth.
              Reach out to our sales team for a demo to discuss your projects
              and complex needs.
            </p>
            <div className="pt-6">
              <Button href="/contact" colour="white">
                Get enterprise demo &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mx-auto space-y-6 text-center w-full pb-6 md:pb-20">
            <h2 className="text-haiti font-bold leading-tight text-3xl md:text-5xl">
              Create. On Your Terms.
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-zodiac">
                GraphCMS unlocks operational efficiency for your team – by
                enabling agile workflows for your content creators and
                developers, to bring your ambitious projects to market faster.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {enablingYouToBuildYourBest.map(
              ({ image, title, description, cta, ctaTo }, index) => (
                <div
                  key={index}
                  className="space-y-3 md:space-y-6 lg:space-y-12"
                >
                  <div className="h-56 flex items-center justify-center">
                    <img src={image} alt="" />
                  </div>
                  <div className="space-y-3 md:space-y-4 py-3">
                    <h3 className="text-haiti font-bold text-xl">{title}</h3>
                    <p className="text-light-haiti">{description}</p>
                    {ctaTo && (
                      <div>
                        <Link
                          to={ctaTo}
                          className="font-medium group text-rose"
                        >
                          {cta || 'Learn more'}
                          <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                            &#8594;
                          </span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

          <div className="pt-10 md:pt-12 flex justify-center">
            <div className="w-full md:w-1/3 md:px-8">
              <Button
                href="/use-cases"
                appearance="outline"
                colour="pink"
                block
              >
                See all solutions &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="space-y-6 text-center w-full">
            <h2 className="text-haiti font-bold leading-tight text-3xl md:text-5xl">
              Manage Content Experiences, the Easy Way
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-zodiac">
                GraphCMS makes it easy to create and manage content that can be
                reused across any channel.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-12 md:pb-6 lg:pt-20">
            {manageContentExperiences.map((usp, index) => (
              <USPCardRedesigned key={index} {...usp} theme="pink" />
            ))}
          </div>

          <div className="text-center lg:-ml-20 lg:-mr-20">
            <img
              src="/illustrations/graphcms-content-studio-pink.svg"
              alt="GraphCMS Content Studio"
              className="w-full"
            />
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20 md:py-24 space-y-12 md:space-y-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="w-full md:w-1/2">
            <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
              Rich Editing Experience
            </h3>
            <p className="text-zodiac">
              The GraphCMS Content Studio comes packed with all the options you
              would want out of a CMS – custom content views, granular
              permissions, content stages, and so much more!
            </p>
          </div>

          <div className="flex flex-col-reverse md:flex-row items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-12 relative">
            <div className="w-full md:w-1/2">
              <div className="space-y-6 py-6">
                {richEditingExperience.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="pink"
                    direction="row"
                  />
                ))}
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <div className="relative inline-flex flex-col justify-center">
                <div className="hidden mx-auto border-b border-px border-rock-blue space-x-6">
                  <button
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-cornflower text-cornflower"
                  >
                    Via Content Studio
                  </button>
                  <button
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-transparent text-light-haiti hover:text-cornflower"
                  >
                    Via API
                  </button>
                  <button
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-transparent text-light-haiti hover:text-cornflower"
                  >
                    Via Custom Frontend
                  </button>
                </div>
                <div className="pt-12 md:-mr-84 md:-ml-16">
                  <img
                    src="/illustrations/via-content-studio.svg"
                    className="block md:ml-10 md:-mt-40"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 md:pt-12">
          <div className="md:flex md:justify-end md:pl-12">
            <div className="w-full md:w-2/5">
              <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
                Draft. Preview. Publish. Repeat.
              </h3>
              <p className="text-zodiac">
                All the content and productivity workflows allowing you to
                publish with confidence.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-12">
            <div className="w-full md:w-3/5 h-full md:flex md:items-center md:justify-center">
              <img
                src="/illustrations/graphcms-content-workflow-pink.svg"
                className="mt-6 md:-mt-32 md:-ml-12"
                alt=""
              />
            </div>
            <div className="w-full md:w-2/5">
              <div className="space-y-6 py-6">
                {draftPreviewPublishRepeat.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="pink"
                    direction="row"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 md:pt-12">
          <div className="w-full md:w-2/5">
            <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
              Localize All Your Content Easily
            </h3>
            <p className="text-zodiac">
              Simple yet powerful localization to translate your content and
              digital assets into any language.
            </p>
          </div>

          <div className="flex flex-col-reverse md:flex-row items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-12 relative">
            <div className="w-full md:w-2/5">
              <div className="space-y-6 py-6">
                {localizeAllYourContentEasily.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="pink"
                    direction="row"
                  />
                ))}
              </div>
            </div>
            <div className="w-full md:w-3/5">
              <div className="md:-mt-24 lg:-mt-32 md:-mr-24">
                <img
                  src="/illustrations/graphcms-content-localization-pink.svg"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="md:flex md:justify-end">
            <div className="w-full md:w-2/5">
              <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
                Granular Roles and Permissions
              </h3>
              <p className="text-zodiac">
                A highly granular permission system that allows you to model
                your organizational structures and application business logic.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0">
            <div className="w-full md:w-3/5 h-full md:flex md:items-center md:justify-center">
              <img
                src="/illustrations/graphcms-content-based-permissions-pink.svg"
                className="mt-6 md:-mt-24 lg:-mt-32 md:-ml-12"
                alt=""
              />
            </div>
            <div className="w-full md:w-2/5">
              <div className="space-y-6 py-6">
                {granularRolesAndPermissions.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="pink"
                    direction="row"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white relative">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          <div className="md:flex md:space-x-12 items-stretch">
            <div className="w-full md:w-2/5 pt-8 pb-4 md:py-32">
              <div className="space-y-6">
                <h2 className="text-haiti font-bold text-3xl lg:text-4xl leading-snug">
                  Don’t Leave Behind Your Favourite Tools
                </h2>
                <p className="text-zodiac text-lg">
                  With GraphCMS’s API Extensions and UI Extensions, be rest
                  assured that all your existing tools, services, and platforms
                  can communicate with GraphCMS.
                </p>
                <Button href="https://app.graphcms.com/signup" colour="pink">
                  Sign up for free &#8594;
                </Button>
              </div>
            </div>
            <div className="w-full md:w-3/5 relative py-4">
              <img
                src="/illustrations/tools.svg"
                alt="Logos of industry tools and software"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-haiti pt-20 pb-10 md:pt-32">
        <div className="max-w-6xl mx-auto px-4 md:px-6 md:pt-12">
          <div className="md:flex md:items-center">
            <div className="w-full md:w-1/2 md:pr-20">
              <div className="relative inline-flex items-center pb-4">
                <span className="text-rose font-medium uppercase tracking-widest text-sm">
                  Outperform your competitor’s digital experiences
                </span>
              </div>
              <h3 className="font-bold text-xl md:text-2xl leading-snug mb-4 text-white">
                "Your customers are literally telling you everything they’re
                looking for [...] having a conversation with your service."
              </h3>
              <p className="text-whisper mb-6">
                We’ve spoken to the leaders of the industry, from companies like
                Algolia, AWS, G2, VWO, Optimove, Commerce Layer, and more, to
                understand where digital experiences are headed, and how a
                Headless CMS forms the foundation of DXPs moving forward.
              </p>
              <Link
                to="/resources/dxp-report-2020"
                className="font-medium group text-rose"
              >
                Download the report
                <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                  &#8594;
                </span>
              </Link>
            </div>

            <div className="w-full md:w-1/2 h-full md:flex md:items-center md:justify-center pt-6 md:pt-0">
              <img src="/illustrations/michael-sokol-pink.svg" alt="" />
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default ForContentCreatorsBaseTemplate;
